<template>
	<div class="plate-volume-chart" style="width: 100%; height: 80px;">
		<v-chart :options="chartOptions" autoresize style="width: 100%; height: 100%;" />
	</div>
</template>

<script>
	import ECharts from 'vue-echarts'
	import 'echarts/lib/chart/line'
    import 'echarts/lib/chart/bar'
	import 'echarts/lib/component/tooltip'
	import 'echarts/lib/component/grid'
	export default {
		name: 'PlateVolumeChart',
		components: {
			'v-chart': ECharts
		},
		props: {
			days: {
				type: Array,
				default: () => []
			},
            trendData: {
                type: Array,
                default: () => []
            }
		},
        data() {
            return {
                chartData: [],
                netInflowData: [], // 资金净流入
                buyData: [], // money_leader_buy
                sellData: [] // money_leader_sell (negative)
            }
        },
        watch: {
            trendData: {
                handler() {
                    this.updateChart();
                },
                deep: true,
                immediate: true
            },
            days: {
                handler(val) {
                    if (val && val.length > 0) this.updateChart();
                }
            }
        },
        methods: {
            updateChart() {
                if (!this.trendData || this.trendData.length === 0 || !this.days || this.days.length === 0) {
                    this.chartData = [];
                    this.netInflowData = [];
                    this.buyData = [];
                    this.sellData = [];
                    return;
                }
                
                const volumeMap = {};
                const inflowMap = {};
                const buyMap = {};
                const sellMap = {};
                this.trendData.forEach(item => {
                    volumeMap[item.date1] = parseFloat(item.trade_money || 0);
                    inflowMap[item.date1] = parseFloat(item.money_leader || 0);
                    buyMap[item.date1] = parseFloat(item.money_leader_buy || 0);
                    sellMap[item.date1] = -Math.abs(parseFloat(item.money_leader_sell || 0)); // 负数向下
                });

                this.chartData = this.days.map(day => {
                    return volumeMap[day] !== undefined ? volumeMap[day] : 0;
                });
                this.netInflowData = this.days.map(day => {
                    return inflowMap[day] !== undefined ? inflowMap[day] : 0;
                });
                this.buyData = this.days.map(day => {
                    return buyMap[day] !== undefined ? buyMap[day] : 0;
                });
                this.sellData = this.days.map(day => {
                    return sellMap[day] !== undefined ? sellMap[day] : 0;
                });
            }
        },
		computed: {
			chartOptions() {
                const self = this;
				return {
					grid: {
						left: '0%', 
						right: '0%',
						top: '25%',
						bottom: '15%',
                        containLabel: false
					},
					xAxis: {
						type: 'category',
						data: this.days,
						show: false,
						boundaryGap: true 
					},
					yAxis: [
                        {
                            type: 'value',
                            show: false,
                            splitLine: { show: false }
                        },
                        {
                            type: 'value',
                            show: false,
                            splitLine: { show: false }
                        }
                    ],
					series: [
                        {
                            name: '成交额',
                            data: this.chartData,
                            type: 'bar', 
                            barWidth: '30%', 
                            yAxisIndex: 0,
                            itemStyle: {
                                color: '#5470c6', 
                                barBorderRadius: [2, 2, 0, 0]
                            },
                            label: {
                                show: true,
                                position: 'top',
                                color: '#ced4da', 
                                fontSize: 10,
                                formatter: function (params) {
                                    const val = Number(params.value);
                                    if (isNaN(val) || val === 0) return '';
                                    if (val > 100000000) {
                                        return (val / 100000000).toFixed(1) + '亿';
                                    } else if (val > 10000) {
                                         return (val / 10000).toFixed(0) + '万';
                                    }
                                    return val;
                                }
                            }
                        },
                        {
                            name: '买入资金',
                            data: this.buyData,
                            type: 'bar',
                            stack: 'inflow',
                            yAxisIndex: 1,
                            barWidth: '25%',
                            barGap: '0%',
                            itemStyle: {
                                color: '#ff4d4f',
                                barBorderRadius: [2, 2, 0, 0]
                            },
                            label: {
                                show: true,
                                position: 'top',
                                fontSize: 9,
                                formatter: (params) => {
                                    const netVal = self.netInflowData[params.dataIndex];
                                    const buyVal = params.value;
                                    if (isNaN(netVal)) return '';
                                    
                                    // 显示净流入
                                    let netText = '';
                                    if (Math.abs(netVal) > 100000000) {
                                        netText = (netVal / 100000000).toFixed(1) + '亿';
                                    } else if (Math.abs(netVal) > 10000) {
                                        netText = (netVal / 10000).toFixed(0) + '万';
                                    } else if (netVal !== 0) {
                                        netText = netVal.toFixed(0);
                                    }
                                    
                                    // 显示买入
                                    let buyText = '';
                                    if (buyVal > 100000000) {
                                        buyText = '+' + (buyVal / 100000000).toFixed(1) + '亿';
                                    } else if (buyVal > 10000) {
                                        buyText = '+' + (buyVal / 10000).toFixed(0) + '万';
                                    } else if (buyVal > 0) {
                                        buyText = '+' + buyVal.toFixed(0);
                                    }
                                    
                                    if (!netText && !buyText) return '';
                                    
                                    let result = '';
                                    if (netText) {
                                        result = netVal > 0 ? '{pos|净:' + netText + '}' : '{neg|净:' + netText + '}';
                                    }
                                    if (buyText) {
                                        result += (result ? '\n' : '') + '{buy|' + buyText + '}';
                                    }
                                    return result;
                                },
                                rich: {
                                    pos: {
                                        color: '#ff4d4f',
                                        fontSize: 9,
                                        fontWeight: 'bold'
                                    },
                                    neg: {
                                        color: '#52c41a',
                                        fontSize: 9,
                                        fontWeight: 'bold'
                                    },
                                    buy: {
                                        color: '#ff7875',
                                        fontSize: 8
                                    }
                                }
                            }
                        },
                        {
                            name: '卖出资金',
                            data: this.sellData,
                            type: 'bar',
                            stack: 'inflow',
                            yAxisIndex: 1,
                            barWidth: '25%',
                            itemStyle: {
                                color: '#52c41a',
                                barBorderRadius: [0, 0, 2, 2]
                            },
                            label: {
                                show: true,
                                position: 'bottom',
                                fontSize: 8,
                                formatter: (params) => {
                                    const val = Math.abs(params.value);
                                    if (isNaN(val) || val === 0) return '';
                                    let text = '';
                                    if (val > 100000000) {
                                        text = '-' + (val / 100000000).toFixed(1) + '亿';
                                    } else if (val > 10000) {
                                        text = '-' + (val / 10000).toFixed(0) + '万';
                                    } else {
                                        text = '-' + val.toFixed(0);
                                    }
                                    return '{sell|' + text + '}';
                                },
                                rich: {
                                    sell: {
                                        color: '#73d13d',
                                        fontSize: 8
                                    }
                                }
                            }
                        }
                    ]
				}
			}
		}
	}
</script>


